import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { AdminColors } from '../../../constants/adminColors';
import FontFamily from '../../../../../constants/fonts';
import { useTheme } from '../../../../../theme/useTheme';
import { Location } from '../locationTypes';

const createPortal = Platform.OS === 'web'
  ? (require('react-dom') as typeof import('react-dom')).createPortal
  : null;

export interface LocationMapTooltipPosition {
  x: number;
  y: number;
}

interface LocationMapTooltipProps {
  location: Location | null;
  position: LocationMapTooltipPosition | null;
  visible: boolean;
}

const HOVER_OFFSET = 14;
const EDGE_PADDING = 8;

function isBlank(value: unknown): boolean {
  return value == null || (typeof value === 'string' && value.trim() === '');
}

function formatTextValue(value: unknown): string {
  if (isBlank(value)) return '·';
  return String(value).trim();
}

function formatNumberValue(value: unknown): string {
  if (isBlank(value)) return '·';

  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '').trim());
  if (Number.isFinite(parsed)) {
    return new Intl.NumberFormat('en-US').format(parsed);
  }

  return String(value).trim();
}

function formatCompactValue(value: unknown): string {
  if (isBlank(value)) return '·';

  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '').trim());
  if (Number.isFinite(parsed)) {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1,
    }).format(parsed);
  }

  return String(value).trim();
}

function format3PlValue(value: unknown): string {
  const text = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (!text) return 'Unknown';
  if (text === 'yes' || text === 'y' || text === 'true' || text === '1' || text === '3pl') return '3PL';
  if (text === 'no' || text === 'n' || text === 'false' || text === '0' || text === 'pepsi / whd' || text === 'pepsi' || text === 'whd') {
    return 'Pepsi / WHD';
  }
  if (text === 'unknown') return 'Unknown';
  return String(value).trim();
}

function formatAddress(location: Location): string {
  const parts = [location.address, location.city, location.state]
    .map(part => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : 'Address not on file';
}

function normalizeUnionStatus(value: unknown): 'Union' | 'Non-Union' | 'Unknown' {
  const text = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (!text) return 'Unknown';
  if (text === 'union' || text === 'yes' || text === 'y' || text === 'true') return 'Union';
  if (text === 'non-union' || text === 'non union' || text === 'no' || text === 'n' || text === 'false') return 'Non-Union';
  return 'Unknown';
}

export function UnionStatusBadge({ value }: { value: unknown }) {
  const { resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === 'dark';
  const status = normalizeUnionStatus(value);
  const badgeStyles = (isDarkTheme ? {
    Union: { backgroundColor: '#1E5A7A', color: '#FFFFFF' },
    'Non-Union': { backgroundColor: '#1A6B24', color: '#FFFFFF' },
    Unknown: { backgroundColor: '#4B4B4B', color: '#FFFFFF' },
  } : {
    Union: { backgroundColor: '#DAEEFA', color: '#1E5A7A' },
    'Non-Union': { backgroundColor: '#DDF9DA', color: '#0C420B' },
    Unknown: { backgroundColor: '#EBEBEB', color: '#4B4B4B' },
  })[status];

  return (
    <View style={[styles.badge, { backgroundColor: badgeStyles.backgroundColor }]}>
      <Text style={[styles.badgeText, { color: badgeStyles.color }]}>{status}</Text>
    </View>
  );
}

function TooltipField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View style={styles.cell}>
      <Text style={styles.label}>{label}</Text>
      {children}
    </View>
  );
}

function TooltipGrid({ location }: { location: Location }) {
  const salesVolume = (location as Location & { salesVolume?: string }).salesVolume;
  const productionVolume = (location as Location & { productionVolume?: string }).productionVolume;

  const fields = useMemo(() => ([
    { label: 'LOC ID', value: formatTextValue(location.id) },
    { label: 'SITE COMPLEXITY', value: formatTextValue(location.facilityType) },
    { label: 'REGION / UNIT', value: formatTextValue(location.org) },
    { label: 'MARKET / POD', value: formatTextValue(location.market) },
    { label: 'WAREHOUSE SYSTEM', value: formatTextValue(location.warehouseSystem) },
    { label: 'REGION LEAD', value: formatTextValue(location.regionLead) },
    { label: 'TOTAL BUILDING SQFT', value: formatNumberValue(location.totalSqft) },
    { label: 'OPERATIONAL SQFT', value: formatNumberValue(location.operationalSqft) },
    { label: 'SALES VOLUME', value: formatCompactValue(salesVolume) },
    { label: 'PRODUCTION VOLUME', value: formatCompactValue(productionVolume) },
    { label: '# OF DOCKS', value: formatNumberValue(location.docks) },
    { label: 'HEADCOUNT', value: formatNumberValue(location.empCount) },
    { label: 'UNION STATUS', value: location.union },
    { label: '3PL / OLC', value: format3PlValue(location.tpl) },
  ]), [location, salesVolume, productionVolume]);

  return (
    <View style={styles.grid}>
      {fields.map(field => (
        <TooltipField key={field.label} label={field.label}>
          {field.label === 'UNION STATUS' ? (
            <UnionStatusBadge value={field.value} />
          ) : (
            <Text style={styles.value}>{field.value}</Text>
          )}
        </TooltipField>
      ))}
    </View>
  );
}

const LocationMapTooltip: React.FC<LocationMapTooltipProps> = ({ location, position, visible }) => {
  const tooltipRef = useRef<View | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<LocationMapTooltipPosition>({ x: 0, y: 0 });
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const initialPosition = position
    ? { x: position.x + HOVER_OFFSET, y: position.y + HOVER_OFFSET }
    : { x: 0, y: 0 };
  const resolvedPosition = tooltipPosition.x === 0 && tooltipPosition.y === 0 ? initialPosition : tooltipPosition;

  useLayoutEffect(() => {
    if (Platform.OS !== 'web') return;

    let container = document.getElementById('location-map-tooltip-portal');
    if (!container) {
      container = document.createElement('div');
      container.id = 'location-map-tooltip-portal';
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.pointerEvents = 'none';
      container.style.zIndex = '60';
      document.body.appendChild(container);
    }
    setPortalContainer(container);
  }, []);

  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || !visible || !location || !position) return;

    const node = tooltipRef.current as any;
    const rect = node?.getBoundingClientRect?.();
    const width = rect?.width ?? 346;
    const height = rect?.height ?? 0;
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : width;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : height;

    let x = position.x + HOVER_OFFSET;
    let y = position.y + HOVER_OFFSET;

    if (x + width > viewportWidth - EDGE_PADDING) {
      x = position.x - width - HOVER_OFFSET;
    }
    if (y + height > viewportHeight - EDGE_PADDING) {
      y = position.y - height - HOVER_OFFSET;
    }

    x = Math.max(EDGE_PADDING, Math.min(x, viewportWidth - width - EDGE_PADDING));
    y = Math.max(EDGE_PADDING, Math.min(y, viewportHeight - height - EDGE_PADDING));

    setTooltipPosition(prev => (prev.x === x && prev.y === y ? prev : { x, y }));
  }, [location, position, visible]);

  if (Platform.OS !== 'web' || !location || !position) {
    return null;
  }

  const tooltipNode = (
    <View
      ref={tooltipRef}
      style={[
        styles.tooltip,
        { left: resolvedPosition.x, top: resolvedPosition.y, opacity: visible ? 1 : 0 },
      ]}
      pointerEvents="none"
    >
      <Text style={styles.name}>{formatTextValue(location.name)}</Text>
      <Text style={styles.sub}>{formatAddress(location)}</Text>
      <TooltipGrid location={location} />
    </View>
  );

  return portalContainer && createPortal
    ? createPortal(tooltipNode, portalContainer)
    : tooltipNode;
};

const styles = StyleSheet.create({
  tooltip: {
    position: 'fixed' as any,
    zIndex: 60,
    width: 346,
    maxWidth: 346,
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    padding: 14,
    fontSize: 12,
    transition: 'opacity 0.1s' as any,
    boxShadow: '0 2px 24px 8px rgba(0, 94, 192, 0.16)' as any,
    pointerEvents: 'none' as any,
  },
  name: {
    fontSize: 14,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.medium,
  },
  sub: {
    fontSize: 11,
    color: AdminColors.textMuted,
    marginTop: 2,
    marginBottom: 10,
    fontFamily: FontFamily.regular,
  },
  grid: {
    display: 'grid' as any,
    gridTemplateColumns: '1fr 1fr' as any,
    gap: '7px 12px' as any,
  },
  cell: {
    minWidth: 0,
  },
  label: {
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    color: AdminColors.textMuted,
    letterSpacing: 0.3,
    fontFamily: FontFamily.medium,
  },
  value: {
    fontSize: 12,
    fontWeight: '600',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.medium,
    fontVariant: ['tabular-nums'] as any,
  },
  badge: {
    alignSelf: 'flex-start',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 1,
    minHeight: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '700',
    fontFamily: FontFamily.medium,
  },
} as any);

export default LocationMapTooltip;
